import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  AvatarGroup,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../AuthContext";
import { useTaskApi } from "./taskApi";
import { tokens } from "../../theme";
import { DEFAULT_FOLDERS, STAGES } from "./taskShared";

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

// One height for every folder column. Tall enough for three or four cards
// before it scrolls, short enough that a row still fits on a laptop.
const COLUMN_HEIGHT = 460;

const STAFF_IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";
const ALL = "__all__";

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const PERMISSION_LABEL = {
  view: "View only",
  edit: "View & edit",
  full: "Full rights",
};

const EMPTY_TASK = {
  title: "",
  description: "",
  department: "",
  visibility: "private",
  priority: "normal",
  start_date: "",
  deadline: "",
  folder_id: "0",
};


/* ─── Helpers ──────────────────────────────────────────────────────────── */

const today = () => new Date().toISOString().split("T")[0];
const num = (v) => parseFloat(v) || 0;

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const daysBetween = (from, to) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const am = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bm = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bm - am) / 86400000);
};

const isOverdue = (t) =>
  t?.status !== "completed" && t?.deadline && daysBetween(today(), t.deadline) < 0;

const initials = (first, last) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || "?";

const atLeast = (have, need) => {
  const rank = { view: 1, edit: 2, full: 3 };
  return (rank[have] || 0) >= (rank[need] || 0);
};

/* ─── Presentational components ────────────────────────────────────────── */
// Module scope on purpose: a component declared inside Dashboard would be a
// new type on every render, remounting these subtrees on each keystroke.

const FormSection = ({ ui, title, hint, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      sx={{
        mb: hint ? 0.35 : 1.5,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 700,
        color: ui.accent,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography variant="h6" sx={{ mb: 1.5, color: ui.textSecondary }}>
        {hint}
      </Typography>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

// Compact on purpose: these are a glance, not the point of the page - the
// columns below are. One line of label, one number, one line of context.
const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.5,
      py: 1.25,
      height: "100%",
      borderRadius: 2,
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderLeft: `3px solid ${tone}`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <Box
      sx={{
        width: 26,
        height: 26,
        flexShrink: 0,
        borderRadius: 1.5,
        display: "grid",
        placeItems: "center",
        color: tone,
        bgcolor: alpha(tone, 0.14),
      }}
    >
      {icon}
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        sx={{
          fontSize: 10,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.textSecondary,
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 800,
          color: ui.textPrimary,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    {sub && (
      <Typography noWrap sx={{ fontSize: 11, color: ui.textSecondary }}>
        {sub}
      </Typography>
    )}
    </Box>
  </Paper>
);


/* ─────────────────────────────────────────────────────────────────────── */

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { userID } = useAuth();
  // Task work runs on the generic CRUD helpers, not on custom PHP actions.
  const callAction = useTaskApi();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get("account") || String(userID || "");
  const folderParam = searchParams.get("folder") || "";
  const shareFlag = searchParams.get("share");
  const newFolderFlag = searchParams.get("newfolder");

  const [permission, setPermission] = useState("view");
  const [isOwn, setIsOwn] = useState(true);
  const [ownerName, setOwnerName] = useState("");
  const [folders, setFolders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [progress, setProgress] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState(ALL);
  const [stageFilter, setStageFilter] = useState(ALL);

  const [openForm, setOpenForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(EMPTY_TASK);
  const [leaderId, setLeaderId] = useState("");
  const [supervisorId, setSupervisorId] = useState("");
  const [collaboratorIds, setCollaboratorIds] = useState([]);
  const [draftSubtasks, setDraftSubtasks] = useState([]);

  const [taskToDelete, setTaskToDelete] = useState(null);

  const [folderDialog, setFolderDialog] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [shareDialog, setShareDialog] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const [shareStaffId, setShareStaffId] = useState("");
  const [sharePermission, setSharePermission] = useState("view");

  const [dragOverFolder, setDragOverFolder] = useState(null);

  const myId = String(userID || "");
  const canEdit = atLeast(permission, "edit");
  const canCreate = atLeast(permission, "full");

  /**
   * Clicking a card opens the task's own page. The account travels in the
   * query string so the page's Back button returns to the board you came
   * from rather than always to your own.
   */
  const openTaskPage = useCallback(
    (task) => navigate(`/tasks/${task.id}?account=${accountId}`),
    [navigate, accountId]
  );

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const priorityColor = useCallback(
    (p) =>
      ({
        low: colors.gray[500],
        normal: colors.newTeal[500],
        high: colors.orange[500],
        urgent: colors.red[500],
      }[p] || colors.gray[500]),
    [colors]
  );

  /* ---------------- Data: one scoped call ---------------- */
  const loadWorkspace = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setLoadError("");

    const res = await callAction("proccess.php", "getTaskWorkspace", {
      owner_id: accountId,
    });

    if (!res.success) {
      setLoadError(res.message || "Could not load this task account");
      setTasks([]);
      setFolders([]);
      setLoading(false);
      return;
    }

    const d = res.data || {};
    setPermission(d.permission || "view");
    setIsOwn(Boolean(Number(d.is_own)));
    setOwnerName(d.owner_name || "");
    setFolders(Array.isArray(d.folders) ? d.folders : []);
    setTasks(Array.isArray(d.tasks) ? d.tasks : []);
    setSubtasks(Array.isArray(d.subtasks) ? d.subtasks : []);
    setMembers(Array.isArray(d.members) ? d.members : []);
    setProgress(Array.isArray(d.progress) ? d.progress : []);
    setLoading(false);
  }, [callAction, accountId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  /**
   * The SQL seeding of To do / In progress / Completed runs once, so a staff
   * member added afterwards opens an account with no folders at all and the
   * board has nowhere to put anything. Create them on first open instead -
   * only in your OWN empty account, and only once.
   *
   * saveTaskFolder is idempotent here: the UNIQUE (owner_id, parent_id, name)
   * key means a duplicate is rejected rather than creating a second "To do".
   */
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    const seedDefaults = async () => {
      if (loading || bootstrapped) return;
      if (!isOwn || folders.length > 0 || loadError) return;

      setBootstrapped(true);
      for (let i = 0; i < DEFAULT_FOLDERS.length; i += 1) {
        await callAction("proccess.php", "saveTaskFolder", {
          owner_id: accountId,
          parent_id: "0",
          name: DEFAULT_FOLDERS[i],
          sort_order: i + 1,
        });
      }
      await loadWorkspace();
      toast.info("Set up your starter folders");
    };
    seedDefaults();
  }, [
    loading,
    bootstrapped,
    isOwn,
    folders.length,
    loadError,
    accountId,
    callAction,
    loadWorkspace,
  ]);

  // Staff list is only needed for the assignment and share pickers.
  const loadStaffs = useCallback(async () => {
    const res = await callAction("proccess.php", "getDatas", {
      table: "staffs",
    });
    if (res.success && Array.isArray(res.data)) setStaffs(res.data);
  }, [callAction]);

  useEffect(() => {
    loadStaffs();
  }, [loadStaffs]);

  const activeStaffs = useMemo(
    () =>
      staffs.filter((s) =>
        ["active", "1"].includes(String(s.status || "").toLowerCase().trim())
      ),
    [staffs]
  );

  const staffById = useCallback(
    (id) => staffs.find((s) => String(s.Id) === String(id)),
    [staffs]
  );

  const staffName = useCallback(
    (id) => {
      const s = staffById(id);
      return s ? `${s.FirstName || ""} ${s.LastName || ""}`.trim() : "";
    },
    [staffById]
  );

  const membersOf = useCallback(
    (taskId) => members.filter((m) => String(m.task_id) === String(taskId)),
    [members]
  );

  const subtasksOf = useCallback(
    (taskId) => subtasks.filter((s) => String(s.task_id) === String(taskId)),
    [subtasks]
  );

  /* ---------------- Columns are FOLDERS ---------------- */
  // With no folder selected, the columns are the root folders. Selecting one
  // in the sidebar drills into it: its subfolders become the columns.
  const byOrder = (a, b) =>
    Number(a.sort_order) - Number(b.sort_order) ||
    String(a.name).localeCompare(String(b.name));

  /** The folder named in the URL, or null at the top level. */
  const openFolder = useMemo(
    () =>
      folderParam
        ? folders.find((f) => String(f.id) === String(folderParam)) || null
        : null,
    [folders, folderParam]
  );

  /**
   * Which columns the board shows.
   *
   * At the top level: every root folder.
   *
   * Inside a folder: the folder ITSELF first, then its subfolders. Showing
   * only the subfolders was wrong - picking a folder with none left the
   * board with no columns at all, so every task counted as a stray and the
   * whole account piled into Unfiled.
   */
  const columnFolders = useMemo(() => {
    if (!openFolder) {
      return folders.filter((f) => Number(f.parent_id) === 0).sort(byOrder);
    }
    const children = folders
      .filter((f) => Number(f.parent_id) === Number(openFolder.id))
      .sort(byOrder);
    return [openFolder, ...children];
  }, [folders, openFolder]);

  /** A folder and everything filed beneath it, however deep. */
  const subtreeIds = useCallback(
    (rootId) => {
      const ids = new Set([String(rootId)]);
      let grew = true;
      while (grew) {
        grew = false;
        folders.forEach((f) => {
          if (ids.has(String(f.parent_id ?? "0")) && !ids.has(String(f.id))) {
            ids.add(String(f.id));
            grew = true;
          }
        });
      }
      return ids;
    },
    [folders]
  );

  /**
   * The intake column - where new work is raised. It is "To do" by name,
   * because that is the folder the setup seeds and the one people mean.
   * If it has been renamed or deleted, the leftmost column stands in, so
   * the button never disappears entirely.
   */
  const intakeFolder = useMemo(
    () =>
      columnFolders.find(
        (f) => String(f.name || "").trim().toLowerCase() === "to do"
      ) || columnFolders[0] || null,
    [columnFolders]
  );

  const isIntake = useCallback(
    (folder) =>
      intakeFolder
        ? Number(folder?.id) === Number(intakeFolder.id)
        : // No folders at all - Unfiled takes the button, so there is
          // always somewhere to raise a task from.
          Number(folder?.id) === 0,
    [intakeFolder]
  );

  const visibleTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks
      .filter((t) => priorityFilter === ALL || t.priority === priorityFilter)
      .filter((t) => stageFilter === ALL || t.status === stageFilter)
      .filter((t) => {
        if (!q) return true;
        return [t.task_code, t.title, t.description, t.department]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      /**
       * Newest first, so work raised today is at the top of its column
       * instead of buried under months of older cards.
       *
       * createDate is a MySQL DATETIME, which needs the space turned into a T
       * before Safari will parse it. Falls back to the id, which is
       * monotonic, when two tasks share a timestamp or one has none.
       */
      .sort((a, b) => {
        const when = (t) => {
          const d = new Date(String(t.createDate || "").replace(" ", "T"));
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        };
        const byDate = when(b) - when(a);
        if (byDate !== 0) return byDate;
        return Number(b.id) - Number(a.id);
      });
  }, [tasks, search, priorityFilter, stageFilter]);

  /**
   * Inside a folder the board only concerns itself with that folder's
   * subtree - otherwise picking "To do" would still show you the whole
   * account, just rearranged.
   */
  const scopedTasks = useMemo(() => {
    if (!openFolder) return visibleTasks;
    const ids = subtreeIds(openFolder.id);
    return visibleTasks.filter((t) => ids.has(String(t.folder_id)));
  }, [visibleTasks, openFolder, subtreeIds]);

  /**
   * A task's percentage is stored, but when it has subtasks the subtasks are
   * the truth - and the stored value only catches up when the rollup writes
   * it. Deriving it here means the bar moves the moment a subtask is ticked.
   */
  const percentOf = useCallback(
    (task) => {
      const subs = subtasks.filter((s) => String(s.task_id) === String(task.id));
      if (!subs.length) return num(task.progress_percent);
      const total = subs.reduce(
        (n, s) => n + (s.status === "completed" ? 100 : num(s.progress_percent)),
        0
      );
      return Math.round(total / subs.length);
    },
    [subtasks]
  );

  const inFolder = useCallback(
    (folderId) =>
      scopedTasks.filter((t) => Number(t.folder_id) === Number(folderId)),
    [scopedTasks]
  );

  // Tasks filed nowhere, or in a folder that is not one of the current
  // columns, would otherwise vanish from the board entirely.
  const strayTasks = useMemo(() => {
    const columnIds = columnFolders.map((f) => Number(f.id));
    return scopedTasks.filter((t) => !columnIds.includes(Number(t.folder_id)));
  }, [scopedTasks, columnFolders]);

  const stats = useMemo(() => {
    const open = visibleTasks.filter((t) => t.status !== "completed");
    return {
      open: open.length,
      inprogress: visibleTasks.filter((t) => t.status === "inprogress").length,
      completed: visibleTasks.filter((t) => t.status === "completed").length,
      overdue: open.filter(isOverdue).length,
      loggedToday: progress.filter(
        (p) => String(p.staff_id) === myId && p.log_date === today()
      ).length,
    };
  }, [visibleTasks, progress, myId]);

  /* ---------------- Sidebar flags ---------------- */
  const clearFlag = useCallback(
    (key) => {
      const next = Object.fromEntries(searchParams);
      delete next[key];
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const openShare = useCallback(async () => {
    setShareDialog(true);
    const res = await callAction("proccess.php", "getTaskAccessList", {
      owner_id: myId,
    });
    setAccessList(res.success && Array.isArray(res.data) ? res.data : []);
  }, [callAction, myId]);

  useEffect(() => {
    if (shareFlag) {
      openShare();
      clearFlag("share");
    }
  }, [shareFlag, openShare, clearFlag]);

  useEffect(() => {
    if (newFolderFlag !== null && newFolderFlag !== undefined) {
      setFolderDialog({ parent_id: newFolderFlag || "0", id: null });
      setFolderName("");
      clearFlag("newfolder");
    }
  }, [newFolderFlag, clearFlag]);

  /* ---------------- Task create / edit ---------------- */
  /**
   * Always opened from the intake column now, so the folder comes from the
   * column you pressed rather than being a field the person has to fill in.
   */
  const openCreate = (folder) => {
    setEditingTask(null);
    setFormData({
      ...EMPTY_TASK,
      start_date: today(),
      deadline: today(),
      folder_id: String(folder?.id ?? intakeFolder?.id ?? "0"),
    });
    setLeaderId("");
    setSupervisorId("");
    setCollaboratorIds([]);
    setDraftSubtasks([]);
    setOpenForm(true);
  };

  const closeForm = () => {
    setOpenForm(false);
    setEditingTask(null);
    setFormData(EMPTY_TASK);
    setDraftSubtasks([]);
  };

  // Clicking the backdrop must not discard a part-filled task.
  const handleFormClose = (_e, reason) => {
    if (reason === "backdropClick") return;
    closeForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addDraftSubtask = () =>
    setDraftSubtasks((prev) => [
      ...prev,
      { title: "", owner: "", deadline: "", priority: "normal" },
    ]);

  const updateDraftSubtask = (i, field, value) =>
    setDraftSubtasks((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s))
    );

  const validate = () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return false;
    }
    if (!formData.start_date || !formData.deadline) {
      toast.error("Start date and deadline are required");
      return false;
    }
    if (daysBetween(formData.start_date, formData.deadline) < 0) {
      toast.error("The deadline cannot be before the start date");
      return false;
    }
    if (draftSubtasks.some((s) => !s.title.trim())) {
      toast.error("Every subtask needs a title");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        owner_id: accountId,
        origin: isOwn ? "staff" : "management",
      };
      if (editingTask) payload.id = editingTask.id;

      // saveTask returns the real insert id, unlike addDatas.
      const res = await callAction("proccess.php", "saveTask", payload);
      if (!res.success) {
        toast.error(res.message || "Could not save the task");
        return;
      }

      const taskId = res.data?.id || editingTask?.id;

      // People. A public task belongs to everyone, so the collaborator
      // list is the whole active roster rather than a hand-picked few -
      // there is no dropdown for it in the form.
      const collaborators =
        formData.visibility === "public"
          ? activeStaffs.map((s) => String(s.Id))
          : collaboratorIds;

      const wanted = [];
      if (leaderId) wanted.push({ staff_id: leaderId, role_on_task: "leader" });
      if (supervisorId)
        wanted.push({ staff_id: supervisorId, role_on_task: "supervisor" });
      collaborators
        .filter((id) => id !== String(leaderId) && id !== String(supervisorId))
        .forEach((id) =>
          wanted.push({ staff_id: id, role_on_task: "collaborator" })
        );

      for (const w of wanted) {
        await callAction("proccess.php", "saveTaskMember", {
          task_id: taskId,
          staff_id: w.staff_id,
          role_on_task: w.role_on_task,
        });
      }

      // Subtasks
      for (let i = 0; i < draftSubtasks.length; i += 1) {
        const d = draftSubtasks[i];
        await callAction("proccess.php", "saveSubtask", {
          task_id: taskId,
          title: d.title.trim(),
          priority: d.priority || "normal",
          deadline: d.deadline || "",
          sort_order: i,
          owner_staff_id: d.owner || "",
        });
      }

      toast.success(
        editingTask
          ? "Task updated"
          : `Task created${res.data?.task_code ? ` (${res.data.task_code})` : ""}`
      );
      await loadWorkspace();
      closeForm();
    } catch (err) {
      toast.error("Network error while saving the task");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Moving ---------------- */
  const moveToFolder = async (task, folderId) => {
    if (!task || Number(task.folder_id) === Number(folderId)) return;
    if (!canEdit) {
      toast.error("You may only view this task account");
      return;
    }

    // Optimistic, so the board does not jump while the request runs.
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, folder_id: folderId } : t))
    );

    const res = await callAction("proccess.php", "moveTask", {
      id: task.id,
      folder_id: folderId,
    });
    if (!res.success) {
      toast.error(res.message || "Could not move the task");
    }
    await loadWorkspace();
  };

  const setStage = async (task, stage) => {
    if (!canEdit) {
      toast.error("You may only view this task account");
      return;
    }
    const res = await callAction("proccess.php", "moveTask", {
      id: task.id,
      status: stage,
    });
    if (!res.success) {
      // The server refuses when a task has subtasks - the rollup cron owns it.
      toast.error(res.message || "Could not change the stage");
      return;
    }
    toast.success(`Marked ${STAGES.find((s) => s.value === stage)?.label}`);
    await loadWorkspace();
  };

  const setSubtaskStage = async (subtask, stage) => {
    const res = await callAction("proccess.php", "saveSubtask", {
      id: subtask.id,
      task_id: subtask.task_id,
      status: stage,
    });
    if (!res.success) {
      toast.error(res.message || "Could not update the subtask");
      return;
    }
    toast.success("Subtask updated");
    await loadWorkspace();
  };

  const confirmDelete = async () => {
    const res = await callAction("proccess.php", "deleteTask", {
      id: taskToDelete.id,
    });
    if (res.success) {
      toast.success("Task deleted");
      await loadWorkspace();
    } else {
      toast.error(res.message || "Could not delete the task");
    }
    setTaskToDelete(null);
  };

  /* ---------------- Folders ---------------- */
  const submitFolder = async () => {
    if (!folderName.trim()) {
      toast.error("Folder name is required");
      return;
    }
    setSaving(true);
    const res = await callAction("proccess.php", "saveTaskFolder", {
      id: folderDialog.id || "",
      owner_id: accountId,
      parent_id: folderDialog.parent_id || "0",
      name: folderName.trim(),
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not save the folder");
      return;
    }
    toast.success(folderDialog.id ? "Folder renamed" : "Folder created");
    setFolderDialog(null);
    setFolderName("");
    await loadWorkspace();
  };

  const removeFolder = async (folder) => {
    const res = await callAction("proccess.php", "deleteTaskFolder", {
      id: folder.id,
    });
    if (res.success) {
      toast.success(res.message || "Folder deleted");
      await loadWorkspace();
    } else {
      toast.error(res.message || "Could not delete the folder");
    }
  };

  /* ---------------- Sharing ---------------- */
  const submitShare = async () => {
    if (!shareStaffId) {
      toast.error("Choose a staff member");
      return;
    }
    setSaving(true);
    const res = await callAction("proccess.php", "shareTaskAccount", {
      grantee_id: shareStaffId,
      permission: sharePermission,
    });
    setSaving(false);

    if (!res.success) {
      toast.error(res.message || "Could not share your account");
      return;
    }
    toast.success("Access granted");
    // The sidebar lists who you have shared with and cannot see this
    // dialog, so tell it to reload rather than leaving it stale.
    window.dispatchEvent(new Event("task-access-changed"));
    setShareStaffId("");
    const list = await callAction("proccess.php", "getTaskAccessList", {
      owner_id: myId,
    });
    setAccessList(list.success && Array.isArray(list.data) ? list.data : []);
  };

  /**
   * shareTaskAccount upserts on (owner, grantee), so re-granting with a
   * different level is how a permission is changed - there is no separate
   * update action to keep in step.
   */
  const changePermission = async (row, permission) => {
    if (permission === row.permission) return;

    // Optimistic: the dropdown should not snap back while the call runs.
    setAccessList((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, permission } : r))
    );

    const res = await callAction("proccess.php", "shareTaskAccount", {
      grantee_id: row.grantee_id,
      permission,
    });

    if (!res.success) {
      setAccessList((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, permission: row.permission } : r))
      );
      toast.error(res.message || "Could not change their permission");
      return;
    }

    toast.success(
      `${row.grantee_name || "They"} now have ${(
        PERMISSION_LABEL[permission] || permission
      ).toLowerCase()}`
    );
    window.dispatchEvent(new Event("task-access-changed"));
  };

  const revokeShare = async (row) => {
    const res = await callAction("proccess.php", "revokeTaskAccess", {
      owner_id: row.owner_id,
      grantee_id: row.grantee_id,
    });
    if (!res.success) {
      toast.error(res.message || "Could not remove access");
      return;
    }
    toast.success("Access removed");
    window.dispatchEvent(new Event("task-access-changed"));
    setAccessList((prev) => prev.filter((r) => r.id !== row.id));
  };

  /* ---------------- Shared style helpers ---------------- */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  /* ---------------- Card ---------------- */

  /**
   * The colour a card wears for its stage. To do keeps the plain card.
   * Overdue is taken from the deadline as well as the status, so a late task
   * shows it even before the rollup has moved it.
   */
  const STAGE_TONE = {
    inprogress: colors.yellow[500],
    overdue: "#f4511e", // orange-red: between the theme's orange and red
    onhold: colors.orange[500],
    completed: colors.green[500],
  };
  const cardStage = (task) => {
    if (task.status === "completed") return "completed";
    if (task.status === "onhold") return "onhold";
    if (task.status === "overdue" || isOverdue(task)) return "overdue";
    if (task.status === "inprogress") return "inprogress";
    return null;
  };

  const renderCard = (task) => {
    const tone = priorityColor(task.priority);
    const overdue = isOverdue(task);
    const subs = subtasksOf(task.id);
    const done = subs.filter((s) => s.status === "completed").length;
    const mem = membersOf(task.id);
    const complete = task.status === "completed";
    const stageTone = STAGE_TONE[cardStage(task)] || null;
    const loggedToday = progress.some(
      (p) =>
        String(p.task_id) === String(task.id) &&
        String(p.staff_id) === myId &&
        p.log_date === today()
    );

    // A task assigned to you from someone else's account is filed in THEIR
    // folders, so it is not draggable here - moving it would take it off
    // their board.
    const foreign = Number(task.is_foreign) === 1;

    return (
      <Paper
        key={task.id}
        elevation={0}
        draggable={canEdit && !foreign}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", String(task.id))}
        onClick={() => openTaskPage(task)}
        sx={{
          p: 1.75,
          mb: 1.5,
          cursor: "pointer",
          borderRadius: 2.5,
          bgcolor: ui.surface,
          // A tint of the stage colour over the card surface, so text keeps
          // its usual contrast. The left edge still carries the priority.
          backgroundImage: stageTone
            ? `linear-gradient(${alpha(stageTone, isDark ? 0.16 : 0.12)}, ${alpha(stageTone, isDark ? 0.16 : 0.12)})`
            : "none",
          border: `1px solid ${stageTone ? alpha(stageTone, 0.6) : ui.border}`,
          borderLeft: `4px solid ${tone}`,
          boxShadow: ui.shadow,
          opacity: complete ? 0.72 : 1,
          transition: "transform .15s ease, box-shadow .15s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: `0 14px 26px -18px ${alpha(tone, 0.9)}`,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 0.75 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            {/* Completion is a flag, not a column - this is the Asana tick. */}
            <Tooltip title={complete ? "Mark not complete" : "Mark complete"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStage(task, complete ? "inprogress" : "completed");
                  }}
                  sx={{ p: 0.25, color: complete ? colors.green[500] : ui.textSecondary }}
                >
                  {complete ? (
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ui.accent2 }}>
              {task.task_code}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5}>
            {task.visibility === "public" ? (
              <Tooltip title="Public - everyone can see this">
                <PublicOutlinedIcon sx={{ fontSize: 15, color: ui.textSecondary }} />
              </Tooltip>
            ) : (
              <Tooltip title="Private">
                <LockOutlinedIcon sx={{ fontSize: 15, color: ui.textSecondary }} />
              </Tooltip>
            )}
            {!loggedToday && task.status === "inprogress" && (
              <Tooltip title="You have not logged progress today">
                <HistoryEduOutlinedIcon
                  sx={{ fontSize: 15, color: colors.orange[500] }}
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: ui.textPrimary,
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {task.title}
        </Typography>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mb: 1.25, flexWrap: "wrap", gap: 0.75 }}
        >
          <Chip
            size="small"
            label={task.priority}
            sx={{
              height: 20,
              fontWeight: 700,
              fontSize: "0.65rem",
              textTransform: "capitalize",
              color: tone,
              bgcolor: alpha(tone, 0.14),
            }}
          />
          <Chip
            size="small"
            label={STAGES.find((s) => s.value === task.status)?.label || task.status}
            sx={{
              height: 20,
              fontWeight: 700,
              fontSize: "0.65rem",
              color: ui.textSecondary,
              bgcolor: alpha(ui.textSecondary, 0.14),
            }}
          />
          {subs.length > 0 && (
            <Chip
              size="small"
              icon={<PlaylistAddCheckOutlinedIcon sx={{ fontSize: 13 }} />}
              label={`${done}/${subs.length}`}
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: "0.65rem",
                color: ui.textSecondary,
                bgcolor: alpha(ui.textSecondary, 0.14),
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          )}
          {Number(task.comment_count) > 0 && (
            <Tooltip
              title={`${task.comment_count} comment${
                Number(task.comment_count) === 1 ? "" : "s"
              }`}
            >
              <Chip
                size="small"
                icon={<ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 13 }} />}
                label={task.comment_count}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            </Tooltip>
          )}
          {foreign && (
            <Tooltip
              title={
                task.origin_folder_name
                  ? `Assigned to you from ${
                      task.from_owner_name || "another account"
                    } — filed there under "${task.origin_folder_name}"`
                  : "Assigned to you from another task account"
              }
            >
              <Chip
                size="small"
                label={`From ${task.from_owner_name || "elsewhere"}`}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: ui.accent2,
                  bgcolor: alpha(ui.accent2, 0.14),
                }}
              />
            </Tooltip>
          )}
        </Stack>

        <LinearProgress
          variant="determinate"
          value={Math.min(100, percentOf(task))}
          sx={{
            height: 5,
            borderRadius: 3,
            mb: 1.25,
            bgcolor: alpha(ui.textSecondary, 0.16),
            "& .MuiLinearProgress-bar": { bgcolor: tone, borderRadius: 3 },
          }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EventOutlinedIcon
              sx={{ fontSize: 14, color: overdue ? colors.red[500] : ui.textSecondary }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: overdue ? 800 : 600,
                color: overdue ? colors.red[500] : ui.textSecondary,
              }}
            >
              {fmtDate(task.deadline)}
            </Typography>
          </Stack>

          <AvatarGroup
            max={3}
            sx={{
              "& .MuiAvatar-root": {
                width: 24,
                height: 24,
                fontSize: 10,
                fontWeight: 700,
                border: `2px solid ${ui.surface}`,
              },
            }}
          >
            {mem.map((m) => {
              const s = staffById(m.staff_id);
              return (
                <Tooltip key={m.id} title={`${m.staff_name || staffName(m.staff_id)} (${m.role_on_task})`}>
                  <Avatar
                    src={s?.image ? `${STAFF_IMAGE_BASE}${s.image}` : ""}
                    sx={{ bgcolor: ui.surfaceAlt, color: ui.textPrimary }}
                  >
                    {initials(s?.FirstName, s?.LastName)}
                  </Avatar>
                </Tooltip>
              );
            })}
          </AvatarGroup>
        </Stack>
      </Paper>
    );
  };

  /* ---------------- One folder column ---------------- */
  const renderColumn = (folder, list, droppable = true) => {
    const over = dragOverFolder === String(folder.id);
    return (
      /**
       * Wraps rather than scrolls. The basis aims for six across, and the
       * 250px minimum stops a column being squeezed below a readable width.
       * Where six would be narrower than that the minimum wins and the rest
       * wrap onto the next row.
       *
       * The minimum is lifted on xs, where the column is full width and a
       * fixed 250px would fight a narrow phone.
       */
      <Box
        key={folder.id}
        sx={{
          flexGrow: 1,
          flexShrink: 0,
          minWidth: { xs: 0, sm: 250 },
          flexBasis: {
            xs: "100%",
            sm: "calc((100% - 12px) / 2)",
            md: "calc((100% - 24px) / 3)",
            lg: "calc((100% - 60px) / 6)",
          },
        }}
      >
        <Paper
          elevation={0}
          onDragOver={(e) => {
            if (!droppable || !canEdit) return;
            e.preventDefault();
            setDragOverFolder(String(folder.id));
          }}
          onDragLeave={() => setDragOverFolder(null)}
          onDrop={(e) => {
            if (!droppable || !canEdit) return;
            e.preventDefault();
            setDragOverFolder(null);
            const id = e.dataTransfer.getData("text/plain");
            moveToFolder(
              tasks.find((t) => String(t.id) === id),
              folder.id
            );
          }}
          sx={{
            p: 1.25,
            // One fixed height for every column, so the board is a tidy grid
            // whatever each one happens to contain. A busy column scrolls
            // inside itself rather than stretching the row it sits in.
            height: COLUMN_HEIGHT,
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            bgcolor: over ? alpha(ui.accent, 0.08) : ui.surfaceAlt,
            border: `1px solid ${over ? alpha(ui.accent, 0.5) : ui.border}`,
            transition: "background-color .15s ease",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.25, flexShrink: 0 }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              {folder.id === 0 ? (
                <InboxOutlinedIcon sx={{ fontSize: 16, color: ui.textSecondary }} />
              ) : (
                <FolderOutlinedIcon sx={{ fontSize: 16, color: ui.accent }} />
              )}
              <Typography
                variant="h6"
                sx={{
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: folder.id === 0 ? ui.textSecondary : ui.accent,
                }}
              >
                {folder.name}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.25} alignItems="center">
              <Chip
                size="small"
                label={list.length}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  color: ui.textSecondary,
                  bgcolor: alpha(ui.textSecondary, 0.18),
                }}
              />
              {canCreate && folder.id !== 0 && (
                <Tooltip title="Delete folder">
                  <IconButton
                    size="small"
                    onClick={() => removeFolder(folder)}
                    sx={{ p: 0.25, color: ui.textSecondary }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {/* New work starts in the intake folder, so the button lives
              there rather than in the page header. */}
          {isIntake(folder) && (
            <Tooltip title={canCreate ? "" : "You may not create tasks here"}>
              <span style={{ display: "block" }}>
                <Button
                  fullWidth
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={() => openCreate(folder)}
                  disabled={!canCreate}
                  sx={{
                    mb: 1.25,
                    py: 0.75,
                    flexShrink: 0,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    color: ui.accent,
                    border: `1px dashed ${alpha(ui.accent, 0.5)}`,
                    "&:hover": {
                      border: `1px dashed ${ui.accent}`,
                      bgcolor: alpha(ui.accent, 0.06),
                    },
                  }}
                >
                  New task
                </Button>
              </span>
            </Tooltip>
          )}

          {/* The only part that scrolls, so the column keeps its height. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              // Hidden bar: the cards themselves show there is more below.
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {list.length === 0 ? (
              <Typography
                variant="h6"
                sx={{ py: 6, textAlign: "center", color: ui.textSecondary }}
              >
                {droppable ? "Drop a task here" : "Nothing here"}
              </Typography>
            ) : (
              list.map(renderCard)
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth={false} sx={{ py: 3.5 }}>
        {/* ---------------- Header ---------------- */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h2"
                sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
              >
                {isOwn ? "Tasks" : `${ownerName || "Shared"} · Tasks`}
              </Typography>
              {!isOwn && (
                <Chip
                  size="small"
                  icon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={PERMISSION_LABEL[permission] || permission}
                  sx={{
                    fontWeight: 700,
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              )}
            </Stack>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              {canCreate
                ? "Drag a card between folders to file it. The tick marks it complete."
                : canEdit
                ? "You can update and move what is here, but not create or delete."
                : "You have read-only access to this account."}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search code, title, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 19, color: ui.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>

        {loadError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              bgcolor: alpha(colors.red[500], 0.1),
              border: `1px solid ${alpha(colors.red[500], 0.45)}`,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.red[500] }}>
              {loadError}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              If the task tables and actions have not been deployed yet, this is
              expected.
            </Typography>
          </Paper>
        )}

        {/* ---------------- KPI cards ---------------- */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              ui={ui}
              tone={ui.accent2}
              icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Open tasks"
              value={stats.open}
              sub={`${stats.completed} completed`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.orange[500]}
              icon={<PendingActionsOutlinedIcon sx={{ fontSize: 18 }} />}
              label="In progress"
              value={stats.inprogress}
              sub="Being worked on now"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              ui={ui}
              tone={stats.overdue ? colors.red[500] : colors.green[500]}
              icon={<ReportProblemOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Overdue"
              value={stats.overdue}
              sub={stats.overdue ? "Past the deadline" : "Nothing late"}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              ui={ui}
              tone={colors.green[500]}
              icon={<TaskAltOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Logged today"
              value={stats.loggedToday}
              sub="Your progress entries"
            />
          </Grid>
        </Grid>

        {/* ---------------- Filters ---------------- */}
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1.25,
            mb: 2,
            borderRadius: 2,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={stageFilter}
              onChange={(_, v) => v !== null && setStageFilter(v)}
              sx={{
                flexWrap: "wrap",
                gap: 0.75,
                "& .MuiToggleButton-root": {
                  px: 1.25,
                  py: 0.3,
                  border: `1px solid ${ui.border} !important`,
                  borderRadius: "999px !important",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 11.5,
                  lineHeight: 1.6,
                  color: ui.textSecondary,
                  bgcolor: ui.surfaceAlt,
                  "&.Mui-selected": {
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                    borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  },
                },
              }}
            >
              <ToggleButton value={ALL}>All stages</ToggleButton>
              {STAGES.map((s) => (
                <ToggleButton key={s.value} value={s.value}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              select
              size="small"
              label="Priority"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 160 } }}
            >
              <MenuItem value={ALL}>All priorities</MenuItem>
              {PRIORITIES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Typography sx={{ mt: 1, fontSize: 11.5, color: ui.textSecondary }}>
            Showing {visibleTasks.length} of {tasks.length} task(s)
            {folderParam ? " · inside the selected folder" : ""}
          </Typography>
        </Paper>

        {/* ---------------- Board: columns are FOLDERS ---------------- */}
        {loading ? (
          // Same shape as the real board, so the layout does not jump when the
          // placeholders are replaced.
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Box
                key={i}
                sx={{
                  flexGrow: 1,
                  flexShrink: 0,
                  minWidth: { xs: 0, sm: 250 },
                  flexBasis: {
                    xs: "100%",
                    sm: "calc((100% - 12px) / 2)",
                    md: "calc((100% - 24px) / 3)",
                    lg: "calc((100% - 60px) / 6)",
                  },
                }}
              >
                <Skeleton
                  variant="rounded"
                  height={COLUMN_HEIGHT}
                  sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "stretch",
                gap: 1.5,
              }}
            >
              {columnFolders.map((f) => renderColumn(f, inFolder(f.id)))}

              {/* Anything filed nowhere, or in a folder outside the current
                  level, still needs somewhere to appear. */}
              {strayTasks.length > 0 &&
                renderColumn({ id: 0, name: "Unfiled" }, strayTasks)}
            </Box>

            {columnFolders.length === 0 && strayTasks.length === 0 && (
              <Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    borderRadius: 3,
                    textAlign: "center",
                    bgcolor: ui.surface,
                    border: `1px dashed ${ui.border}`,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                    No folders here yet
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
                    {canCreate
                      ? "Use the New folder button in the sidebar to add one."
                      : "Nothing has been filed in this account."}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Create / edit task                                              */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openForm}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {editingTask ? "Edit task" : "New task"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: ui.border }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            <FormSection ui={ui} title="1 · Basics">
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  sx={fieldSx}
                />
              </Grid>
              {/* No folder picker: a new task is raised in the intake
                  column, and afterwards it is filed by dragging it. */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Visibility"
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleFormChange}
                  helperText={
                    formData.visibility === "public"
                      ? "Every active staff member is added as a collaborator"
                      : "Only this account and those it is shared with"
                  }
                  sx={fieldSx}
                >
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleFormChange}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            <FormSection
              ui={ui}
              title="2 · People"
              hint="Leave empty to work alone."
            >
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Task leader"
                  value={leaderId}
                  onChange={(e) => setLeaderId(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {activeStaffs.map((s) => (
                    <MenuItem key={s.Id} value={String(s.Id)}>
                      {s.FirstName} {s.LastName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Supervisor"
                  value={supervisorId}
                  onChange={(e) => setSupervisorId(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {activeStaffs.map((s) => (
                    <MenuItem key={s.Id} value={String(s.Id)}>
                      {s.FirstName} {s.LastName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {/* A public task is everyone's, so there is nothing to
                  choose - every active staff member is added on save. */}
              <Grid size={{ xs: 12, sm: 4 }}>
                {formData.visibility === "public" ? (
                  <Box
                    sx={{
                      height: "100%",
                      minHeight: 56,
                      px: 1.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderRadius: "10px",
                      border: `1px dashed ${alpha(ui.accent, 0.5)}`,
                      bgcolor: alpha(ui.accent, 0.06),
                    }}
                  >
                    <PublicOutlinedIcon sx={{ fontSize: 18, color: ui.accent }} />
                    <Typography variant="body2" sx={{ color: ui.textSecondary }}>
                      All {activeStaffs.length} active staff
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    select
                    fullWidth
                    label="Collaborators"
                    value={collaboratorIds}
                    onChange={(e) =>
                      setCollaboratorIds(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value
                      )
                    }
                    SelectProps={{ multiple: true }}
                    sx={fieldSx}
                  >
                    {activeStaffs.map((s) => (
                      <MenuItem key={s.Id} value={String(s.Id)}>
                        {s.FirstName} {s.LastName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
            </FormSection>

            <FormSection ui={ui} title="3 · Schedule & priority">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  sx={fieldSx}
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </FormSection>

            <FormSection
              ui={ui}
              title="4 · Subtasks"
              hint="A task with subtasks follows them - it completes when they all do."
            >
              <Grid size={{ xs: 12 }}>
                {draftSubtasks.map((d, i) => (
                  <Stack
                    key={i}
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    sx={{ mb: 1.5 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={`Subtask ${i + 1}`}
                      value={d.title}
                      onChange={(e) => updateDraftSubtask(i, "title", e.target.value)}
                      sx={fieldSx}
                    />
                    <TextField
                      select
                      size="small"
                      label="Owner"
                      value={d.owner}
                      onChange={(e) => updateDraftSubtask(i, "owner", e.target.value)}
                      sx={{ ...fieldSx, minWidth: { md: 180 } }}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {activeStaffs.map((s) => (
                        <MenuItem key={s.Id} value={String(s.Id)}>
                          {s.FirstName} {s.LastName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="date"
                      label="Deadline"
                      value={d.deadline}
                      onChange={(e) => updateDraftSubtask(i, "deadline", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ ...fieldSx, minWidth: { md: 160 } }}
                    />
                    <IconButton
                      onClick={() =>
                        setDraftSubtasks((prev) => prev.filter((_, x) => x !== i))
                      }
                      sx={{ color: colors.red[500] }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={addDraftSubtask}
                  sx={outlineBtn(ui.accent)}
                >
                  Add subtask
                </Button>
              </Grid>
            </FormSection>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={closeForm}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : editingTask ? "Update task" : "Create task"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!folderDialog}
        onClose={() => setFolderDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {folderDialog?.id ? "Rename folder" : "New folder"}
          </Typography>
          {folderDialog?.parent_id && folderDialog.parent_id !== "0" && (
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Inside{" "}
              {folders.find((f) => String(f.id) === String(folderDialog.parent_id))
                ?.name || "a folder"}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            sx={{ ...fieldSx, mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setFolderDialog(null)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button onClick={submitFolder} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : "Save folder"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Share my tasks                                                  */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            Share my tasks
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
            Only you can grant access to your own account.
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: ui.border }}>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Staff member"
                value={shareStaffId}
                onChange={(e) => setShareStaffId(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">Choose…</MenuItem>
                {activeStaffs
                  .filter((s) => String(s.Id) !== myId)
                  .map((s) => (
                    <MenuItem key={s.Id} value={String(s.Id)}>
                      {s.FirstName} {s.LastName}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Permission"
                value={sharePermission}
                onChange={(e) => setSharePermission(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="view">View only</MenuItem>
                <MenuItem value="edit">View & edit</MenuItem>
                <MenuItem value="full">Full rights</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button onClick={submitShare} disabled={saving} sx={primaryBtn}>
                {saving ? "Saving…" : "Grant access"}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: ui.border, mb: 2 }} />

          <Typography
            variant="h6"
            sx={{
              mb: 1.5,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              fontWeight: 700,
              color: ui.accent,
            }}
          >
            Who has access
          </Typography>

          {accessList.length === 0 ? (
            <Typography variant="h5" sx={{ color: ui.textSecondary, fontWeight: 600 }}>
              You have not shared your tasks with anyone.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {accessList.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${ui.border}`,
                    bgcolor: alpha(ui.surfaceAlt, isDark ? 0.5 : 0.7),
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={
                        row.grantee_image
                          ? `${STAFF_IMAGE_BASE}${row.grantee_image}`
                          : ""
                      }
                      sx={{ width: 30, height: 30, fontSize: 12 }}
                    >
                      {initials(
                        row.grantee_name?.split(" ")[0],
                        row.grantee_name?.split(" ")[1]
                      )}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                        {row.grantee_name}
                      </Typography>
                      {row.grantee_department && (
                        <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                          {row.grantee_department}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      select
                      size="small"
                      value={row.permission}
                      onChange={(e) => changePermission(row, e.target.value)}
                      sx={{ ...fieldSx, minWidth: 150 }}
                    >
                      {Object.keys(PERMISSION_LABEL).map((p) => (
                        <MenuItem key={p} value={p}>
                          {PERMISSION_LABEL[p]}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Tooltip title="Remove access">
                    <IconButton
                      size="small"
                      onClick={() => revokeShare(row)}
                      sx={{ color: colors.red[500] }}
                    >
                      <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setShareDialog(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete task                                                     */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            Delete task
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ color: ui.textSecondary, lineHeight: 1.6 }}>
            Permanently delete{" "}
            <Box component="span" sx={{ fontWeight: 800, color: ui.textPrimary }}>
              {taskToDelete?.task_code} — {taskToDelete?.title}
            </Box>
            ? Its subtasks, assignments and progress logs go with it. This cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setTaskToDelete(null)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
